/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useImperativeHandle, useMemo, useEffect } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { DataType, addClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { findMachineSummary, saveUtMatrixDemand } from 'apis/ut/UtMatrixRegist';
import { UtMatrixDetail, UtMatrixRegistSearchCondition } from 'models/ut/UtMatrix';
import { hasRole } from 'utils/SessionUtil';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { useMessageBar } from 'hooks/useMessageBar';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';
import { convertNumber } from 'utils/UtMatrixUtil';
import { CrudCode } from 'models/common/Edit';
import { useCommonModal } from 'hooks/useCommonModal';

/**
 * @desc 검토/결재 > [기계요약] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.01   jshwang12       [고도화] CDA, N2 단위 변경 (Nl/min)
 * 2025.10.01   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리) (ex. 230.456 입력 시 실제 데이터는 230.5 저장)
 * 2025.10.04   jshwang12       N2 Peak 수용율 수정 시 N2 AVG 수용율 동일하게 변경
 * 2026.01.26   jihwan89        CSR E20260106-000678 검토화면 천단위 숫자 인식되게 변경(컴마삭제)
 * 2026.02.09   jihwan89        수용율 변경 후 재작성요청, 보류, 검증, 검토진행 시 알림 메세지 오픈 및 임시저장
 */
const MachineSummaryContent = (props: any, ref) => {
  const {
    onSubmit,
    condition = {} as UtMatrixRegistSearchCondition,
    processData = [] as UtMatrixDetail[],
  } = props;
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [rowData, setRowData] = useState<UtMatrixDetail[]>([]);
  const [flexRef, setFlexRef] = useState<any>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const defaultGridHeight = 241 + 30;
  const isAprText = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP05');
    return condition.utmWkProgStatCd === 'UTM04' && p.length > 0
      ? String(t('com.button.결재정보', '결재정보'))
      : String(t('com.button.결재요청', '결재요청'));
  }, [processData, condition.utmWkProgStatCd]);
  //검토중
  const isUtReview = useMemo(() => {
    return condition.utmWkProgStatCd === 'UTM04' && condition.openMode !== 'appr' && hasAuth;
  }, [condition.utmWkProgStatCd, hasAuth]);

  // 2025.09.16 sungim.kim2 검토완료 <재작성요청><보류><결재요청>
  const isUtReviewComplete = useMemo(() => {
    return condition.utmWkProgStatCd === 'UTM07' && condition.openMode !== 'appr' && hasAuth;
  }, [condition.utmWkProgStatCd, hasAuth]);

  // 작성완료 : <검토진행> <재작성요청> <보류> <목록> <다운로드>
  const isUtComplete = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP03');

    return condition.utmWkProgStatCd === 'UTM03' && hasAuth;
  }, [processData, condition.utmWkProgStatCd, hasAuth]);

  // 작성중 : <검증> <임시저장> <새로고침>
  const isUtWriter = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP02');
    return condition.utmWkProgStatCd === 'UTM02' && hasAuth; //작성중
  }, [processData, condition.utmWkProgStatCd, hasAuth]);

  useImperativeHandle(ref, () => ({
    searchMachineSummary: (condition) => {
      searchMachineSummary(condition);
    },
  }));

  useEffect(() => {
    if (hasRole('ADM') || hasRole('UT_MANAGER')) {
      setHasAuth(true);
    }
  }, []);

  const searchMachineSummary = (condition) => {
    findMachineSummary(condition).then((result) => {
      setRowData(result || []);
    });
  };

  const layoutDefinition = useMemo(() => {
    const calcuateActualLoad = (
      targetColNm = '',
      totalSumCd = 'Total Sum',
      demandCd = 'demand'
    ) => {
      const demandRows = (rowData || []).filter((o) => o.prdnProcCd === 'demand');
      const totalSumRows = (rowData || []).filter((o) => o.eqclId === 'Total Sum');
      const demandRow = demandRows.length > 0 ? demandRows[0] : null;
      const totalSumRow = totalSumRows.length > 0 ? totalSumRows[0] : null;
      if (demandRow && totalSumRow && targetColNm) {
        const demand = convertNumber(demandRow[targetColNm] || 0);
        const totalSum = convertNumber(totalSumRow[targetColNm] || 0);
        //return parseFloat((totalSum * demand).toFixed(1)).toLocaleString();
        return parseFloat(((totalSum * demand) / 100).toFixed(1)).toLocaleString();
      }
      return '';
    };
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        width: 43,
        isReadOnly: true,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('ut.label.공정', '공정')),
        width: 100,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'eqclNm',
        header: String(t('ut.label.설비군', '설비군')),
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'qty',
        header: String(t('ut.label.라인/설비 수량', '라인/설비 수량')),
        width: 120,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (params) =>
          ['demand', 'actualLoad'].includes(params.item.prdnProcCd)
            ? ''
            : `${(params.item.eqpPrdnLnQty || 0).toLocaleString()} / ${(
                params.item.machEqpQty || 0
              ).toLocaleString()}`,
      },
      {
        header: String(t('ut.label.CDA', 'CDA')),
        columns: [
          {
            binding: 'cdaDmdRate',
            header: '[Nl/min]',
            minWidth: 100,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('cdaDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.N2 Peak', 'N2 Peak')),
        columns: [
          {
            binding: 'nitrDmdRate',
            header: '[Nl/min]',
            minWidth: 95,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('nitrDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.N2 AVG', 'N2 AVG')),
        columns: [
          {
            binding: 'nitrAvgDmdRate', // 기계 조건부 필수 대상값
            header: '[Nl/min]',
            width: 95,
            isReadOnly: true,
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('nitrAvgDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.IW', 'IW')),
        columns: [
          {
            binding: 'iwDmdRate',
            header: '[ℓ/min]',
            minWidth: 100,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('iwDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.IW2', 'FW')),
        columns: [
          {
            binding: 'frwtDmdRate',
            header: '[ℓ/min]',
            minWidth: 100,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('frwtDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.PCW', 'PCW')),
        columns: [
          {
            binding: 'coolDmdRate',
            header: '[ℓ/min]',
            minWidth: 100,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('coolDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.WW', 'WW')),
        columns: [
          {
            binding: 'wwDmdRate',
            header: '[ℓ/min]',
            minWidth: 100,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('wwDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.Steam', 'Steam')),
        columns: [
          {
            binding: 'steamDmdRate',
            header: '[t/hr]',
            minWidth: 100,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('steamDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.NG', 'NG')),
        columns: [
          {
            binding: 'lngDmdRate',
            header: '[N㎥/hr]',
            minWidth: 100,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('lngDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.Return air', 'Return air')),
        columns: [
          {
            binding: 'insdExhaDmdRate',
            header: '[CMH]',
            minWidth: 150,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('insdExhaDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.Exhaust air', 'Exhaust air')),
        columns: [
          {
            binding: 'otsdExhaDmdRate',
            header: '[CMH]',
            minWidth: 150,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('otsdExhaDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
      {
        header: String(t('ut.label.Supply Air', 'Supply air')),
        columns: [
          {
            binding: 'suarDmdRate',
            header: '[CMH]',
            minWidth: 150,
            width: '*',
            dataType: DataType.Number,
            format: 'n1',
            cellTemplate: (params) =>
              'actualLoad' === params.item.prdnProcCd
                ? calcuateActualLoad('suarDmdRate')
                : DefaultZeroNumberCellTemplate(params),
          },
        ],
      },
    ];
  }, [rowData]);

  const btnExcelExportGrid = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
      formatItem: exportFormat,
    });
    book.sheets[0].name = 'Ut Matrix 검토 기계 요약';
    book.saveAsync(
      getExcelFileName(t('ut.label.Ut Matrix 검토 기계 요약', 'Ut Matrix 검토 기계 요약'))
    );
  };

  const exportFormat = (args) => {
    const xlsxCell = args.xlsxCell;
    const cell = args.getFormattedCell();
    // xlsxCell.value = cell.textContent;
    const delCommaValue = cell.textContent?.replace(/,/g, '');
    xlsxCell.value = delCommaValue;
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (binding) {
        if (item.eqclNm === 'Sub Sum') {
          addClass(cell, 'rowTotal');
        } else if (item.eqclNm === 'Total Sum') {
          addClass(cell, 'sumTotal');
        }
      }

      // readonly 설정
      if (
        [
          'cdaDmdRate',
          'nitrDmdRate',
          'iwDmdRate',
          'frwtDmdRate',
          'coolDmdRate',
          'wwDmdRate',
          'steamDmdRate',
          'lngDmdRate',
          'insdExhaDmdRate',
          'otsdExhaDmdRate',
          'suarDmdRate',
        ].includes(binding)
      ) {
        const readonly = item.prdnProcCd !== 'demand';
        cell.ariaReadOnly = readonly;
      }
    }
  });
  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.formatItem.addHandler(function (s, e) {
      if (s.cells === e.panel) {
        const column = s.columns[e.col];
        const format = column.format;
        const dataType = column.dataType;
        if (dataType === DataType.Number && format) {
          // [25.10.01] 자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리) (ex. 230.456 입력 시 실제 데이터는 230.5 저장)
          const binding = column.binding;
          s.rows[e.row].dataItem[binding] = e.cell.innerText ? convertNumber(e.cell.innerText) : '';
        }
      }
    });
  };

  const handleSave = () => {
    return new Promise((resolve) => {
      if (!condition?.utmId) {
        openMessageBar({
          type: 'error',
          content: t('com.label.잘못된 접근입니다.', '잘못된 접근입니다.'),
        });
        return;
      }

      const rows = (rowData || []).filter((o) => o.crudKey);
      if (!rows.length) {
        resolve(true);
        return;
      }

      saveUtMatrixDemand({ ...rows[0], utmId: condition?.utmId })
        .then((res) => {
          if (res) {
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('com.msg.저장되었습니다.', '저장되었습니다.')
                  : t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          }
          if (res?.successOrNot === 'Y') {
            searchMachineSummary(condition);
          }
        })
        .finally(() => resolve(true));
    });
  };
  //검토완료
  const handleSubmitReviewComplete = () => {
    props?.onSubmitComplete?.();
  };
  //검토진행
  const handleSubmitReview = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: async () => {
          await handleSave();
          props?.onSubmit?.();
        },
      });
    } else {
      props?.onSubmit?.();
    }
  };

  //검증
  const handleModify = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: async () => {
          await handleSave();
          props?.onModify?.();
        },
      });
    } else {
      props?.onModify?.();
    }
  };
  //보류 링크
  const handleRequestLinkHold = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: async () => {
          await handleSave();
          props?.onLinkHold?.();
        },
      });
    } else {
      props?.onLinkHold?.();
    }
  };
  //재작성 링크
  const handleRequestLinkRewrite = () => {
    const fixedData = rowData.filter((o) => o.crudKey === CrudCode.UPDATE);
    if (fixedData.length > 0) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          'com.label.변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?',
          '변경된 수용율이 있습니다. 저장 후 진행하시겠습니까?'
        ),
        yesCallback: async () => {
          await handleSave();
          props?.onLinkRewrite?.();
        },
      });
    } else {
      props?.onLinkRewrite?.();
    }
  };

  const handleApproval = () => {
    props?.onAppr?.();
  };

  const onCellEditEnding = useEvent((grid, e) => {
    const newVal = grid.activeEditor?.value;
    const data = grid.rows[e.row].dataItem;
    const binding = grid.columns[e.col].binding;
    // [25.10.04] N2 Peak 수용율 수정 시 N2 AVG 수용율 동일하게 변경
    if ('nitrDmdRate' === binding) {
      // N2 AVG 수용율은 따로 관리하지 않기 때문에 화면에서 실시간 적용을 위해 동일하게 값 변경
      data.nitrAvgDmdRate = newVal;
    }
  });

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.기계요약', '기계요약')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {condition?.utmNo && <div className="info warning">{condition?.utmNo}</div>}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExportGrid}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => searchMachineSummary(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={props?.isOpenCondition ? defaultGridHeight + 56 : defaultGridHeight + 60 + 196 + 71}
        frozenColumns={4}
        isSelector={false}
        allowPinning={false}
        initialized={onInitialized}
        beginningEdit={(grid, e) => {
          const binding = grid.columns[e.col].binding;
          const item = grid.rows[e.row].dataItem;
          // readonly 설정
          if (
            [
              'cdaDmdRate',
              'nitrDmdRate',
              'nitrAvgDmdRate',
              'iwDmdRate',
              'frwtDmdRate',
              'coolDmdRate',
              'wwDmdRate',
              'steamDmdRate',
              'lngDmdRate',
              'insdExhaDmdRate',
              'otsdExhaDmdRate',
              'suarDmdRate',
            ].includes(binding) &&
            item.prdnProcCd !== 'demand'
          ) {
            // 수용률만 key-in 처리
            e.cancel = true;
          }
        }}
        cellEditEnding={onCellEditEnding}
      />

      <GlobalBtnGroup>
        {/* 작성중 : <검증> <임시저장> <새로고침> UTM02  */}
        {/* 작성완료 : <검토진행> <재작성요청> <보류> <목록> <다운로드>  UTM05 */}
        {/* 검토중 : <결재요청> <목록> <다운로드>  UTM04 */}
        {/* 그외 (결재중) : <목록> <다운로드>  */}

        {isUtWriter && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={() => handleSave()}>
              {t('com.button.임시저장', '임시저장')}
            </Button>
          </>
        )}
        {isUtComplete && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleSubmitReview}>
              {t('com.button.검토진행', '검토진행')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
          </>
        )}
        {isUtReview && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleSubmitReviewComplete}>
              {t('com.button.검토완료', '검토완료')}
            </Button>
            {/*<Button css={IconButton.button} className="save" onClick={handleApproval}>*/}
            {/*  {isAprText}*/}
            {/*</Button>*/}
          </>
        )}

        {/*2025.09.16 sungim.kim2 검토완료 상태 일때 버튼 정의*/}
        {isUtReviewComplete && (
          // <재작성요청><보류><결재요청>
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="save" onClick={handleApproval}>
              {isAprText}
            </Button>
          </>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default React.forwardRef(MachineSummaryContent);
